import { useHandler } from "@redotech/react-util/hook";
import {
  CarrierAccountType,
  ICarrierAccount,
  RedoCarrierName,
} from "@redotech/redo-model/carrier-account/carrier-account";
import { ICreateCarrierAccount } from "@redotech/redo-model/carrier-account/create-carrier-account";
import { assertNever } from "@redotech/util/type";
import { toast } from "../../../alert";
import { CarrierInputValue } from "./carrier-input";

type CarrierAccountResponse = {
  merchantCarrierAccounts: ICarrierAccount[];
  redoChildCarrierAccounts: ICarrierAccount[];
};

export function useCarriers({
  carrierAccountType,
  setSavedCarriers,
  createCarrierAccount,
  deleteCarrierAccount,
}: {
  carrierAccountType: CarrierAccountType;
  setSavedCarriers: (carriers: ICarrierAccount[]) => void;
  createCarrierAccount: (
    carrier: ICreateCarrierAccount,
  ) => Promise<CarrierAccountResponse>;
  deleteCarrierAccount: ({
    carrierAccount,
  }: {
    carrierAccount: ICarrierAccount;
  }) => Promise<CarrierAccountResponse>;
}) {
  const getSavedCarriers = useHandler((response: CarrierAccountResponse) => {
    switch (carrierAccountType) {
      case CarrierAccountType.MERCHANT:
        return response.merchantCarrierAccounts;
      case CarrierAccountType.REDO_CHILD:
        return response.redoChildCarrierAccounts;
      default:
        assertNever(carrierAccountType);
    }
  });

  const addCarrier = useHandler(async (carrier: CarrierInputValue) => {
    try {
      let carrierToAdd: ICreateCarrierAccount;
      switch (carrier.redoCarrierName) {
        case RedoCarrierName.FIRST_MILE: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.firstMileCarrierInput.accountNumber,
              username: carrier.firstMileCarrierInput.username,
              password: carrier.firstMileCarrierInput.password,
            },
          };
          break;
        }
        case RedoCarrierName.AUSTRALIA_POST: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.australiaPostCarrierInput.accountNumber,
              api_key: carrier.australiaPostCarrierInput.apiKey,
              api_secret: carrier.australiaPostCarrierInput.apiSecret,
            },
          };
          break;
        }
        case RedoCarrierName.CANADA_POST: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              api_key: carrier.canadaPostCarrierInput.apiKey,
              contract_id: carrier.canadaPostCarrierInput.contractId,
              customer_number: carrier.canadaPostCarrierInput.customerNumber,
              has_credit_card: carrier.canadaPostCarrierInput.hasCreditCard,
            },
          };
          break;
        }
        case RedoCarrierName.ROYAL_MAIL: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.royalMailCarrierInput.accountNumber,
              posting_location_number:
                carrier.royalMailCarrierInput.postingLocationNumber,
              username: carrier.royalMailCarrierInput.username,
            },
          };
          break;
        }
        case RedoCarrierName.ROYAL_MAIL_V3: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.royalMailV3CarrierInput.accountNumber,
              oba_email_address:
                carrier.royalMailV3CarrierInput.obaEmailAddress,
              posting_location_number:
                carrier.royalMailV3CarrierInput.postingLocationNumber,
              oba_access_code:
                carrier.royalMailV3CarrierInput.obaAccessCode || undefined,
              registered_billing_postcode:
                carrier.royalMailV3CarrierInput.registeredBillingPostcode ||
                undefined,
              contact_name: carrier.royalMailV3CarrierInput.contactName,
              contact_phone: carrier.royalMailV3CarrierInput.contactPhone,
              dispatch_street1: carrier.royalMailV3CarrierInput.dispatchStreet1,
              dispatch_street2:
                carrier.royalMailV3CarrierInput.dispatchStreet2 || undefined,
              dispatch_city: carrier.royalMailV3CarrierInput.dispatchCity,
              dispatch_zip: carrier.royalMailV3CarrierInput.dispatchZip,
              dispatch_state: carrier.royalMailV3CarrierInput.dispatchState,
              dispatch_country: carrier.royalMailV3CarrierInput.dispatchCountry,
            },
          };
          break;
        }
        case RedoCarrierName.VEHO: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              api_key: carrier.vehoCarrierInput.productionApiKey,
            },
          };
          break;
        }
        case RedoCarrierName.APC: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              shipper_id: carrier.apcCarrierInput.shipperId,
              password: carrier.apcCarrierInput.password,
              facility: carrier.apcCarrierInput.facility,
            },
          };
          break;
        }
        case RedoCarrierName.DHL_ECS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              client_id: carrier.dhlCarrierInput.clientId,
              client_secret: carrier.dhlCarrierInput.clientSecret,
              distribution_center: carrier.dhlCarrierInput.distributionCenter,
              pickup_id: carrier.dhlCarrierInput.pickupId,
            },
          };
          break;
        }
        case RedoCarrierName.DHL_EXPRESS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.dhlExpressCarrierInput.accountNumber,
              country_code: carrier.dhlExpressCarrierInput.countryCode,
              password: carrier.dhlExpressCarrierInput.password,
              site_id: carrier.dhlExpressCarrierInput.siteId,
            },
          };
          break;
        }
        case RedoCarrierName.UPS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.upsCarrierInput.accountNumber,
            },
          };
          break;
        }
        case RedoCarrierName.UPS_SUREPOST: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.upsSurepostCarrierInput.accountNumber,
            },
          };
          break;
        }
        case RedoCarrierName.UPS_MAIL_INNOVATIONS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number:
                carrier.upsMailInnovationsCarrierInput.accountNumber,
              user_id: carrier.upsMailInnovationsCarrierInput.userId,
              password: carrier.upsMailInnovationsCarrierInput.password,
              customer_id: carrier.upsMailInnovationsCarrierInput.customerId,
              customer_guid:
                carrier.upsMailInnovationsCarrierInput.customerGuid,
            },
          };
          break;
        }
        case RedoCarrierName.USPS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              company_name: carrier.uspsCarrierInput.companyName,
              address_street: carrier.uspsCarrierInput.addressStreet,
              address_city: carrier.uspsCarrierInput.addressCity,
              address_state: carrier.uspsCarrierInput.addressState,
              address_zip: carrier.uspsCarrierInput.addressZip,
              phone: carrier.uspsCarrierInput.phone,
              email: carrier.uspsCarrierInput.email,
              shipper_id: carrier.uspsCarrierInput.shipperId,
            },
          };
          break;
        }
        case RedoCarrierName.FEDEX_CROSS_BORDER: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              api_key: carrier.fedexCrossBorderCarrierInput.apiKey,
              connect_hub_origin:
                carrier.fedexCrossBorderCarrierInput.connectHubOrigin,
            },
          };
          break;
        }
        case RedoCarrierName.FEDEX: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.fedexCarrierInput.accountNumber,
              shipping_city: carrier.fedexCarrierInput.shippingCity,
              shipping_country_code:
                carrier.fedexCarrierInput.shippingCountryCode,
              shipping_postal_code:
                carrier.fedexCarrierInput.shippingPostalCode,
              shipping_state: carrier.fedexCarrierInput.shippingState,
              shipping_streets: carrier.fedexCarrierInput.shippingStreets,
              corporate_city: carrier.fedexCarrierInput.corporateCity,
              corporate_company_name:
                carrier.fedexCarrierInput.corporateCompanyName,
              corporate_country_code:
                carrier.fedexCarrierInput.corporateCountryCode,
              corporate_email_address:
                carrier.fedexCarrierInput.corporateEmailAddress,
              corporate_first_name:
                carrier.fedexCarrierInput.corporateFirstName,
              corporate_job_title: carrier.fedexCarrierInput.corporateJobTitle,
              corporate_last_name: carrier.fedexCarrierInput.corporateLastName,
              corporate_phone_number:
                carrier.fedexCarrierInput.corporatePhoneNumber,
              corporate_postal_code:
                carrier.fedexCarrierInput.corporatePostalCode,
              corporate_state: carrier.fedexCarrierInput.corporateState,
              corporate_streets: carrier.fedexCarrierInput.corporateStreets,
            },
          };
          break;
        }
        case RedoCarrierName.FEDEX_SMARTPOST: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.fedexSmartpostCarrierInput.accountNumber,
              hub_id: carrier.fedexSmartpostCarrierInput.hubId,
              shipping_city: carrier.fedexSmartpostCarrierInput.shippingCity,
              shipping_country_code:
                carrier.fedexSmartpostCarrierInput.shippingCountryCode,
              shipping_postal_code:
                carrier.fedexSmartpostCarrierInput.shippingPostalCode,
              shipping_state: carrier.fedexSmartpostCarrierInput.shippingState,
              shipping_streets:
                carrier.fedexSmartpostCarrierInput.shippingStreets,
              corporate_city: carrier.fedexSmartpostCarrierInput.corporateCity,
              corporate_company_name:
                carrier.fedexSmartpostCarrierInput.corporateCompanyName,
              corporate_country_code:
                carrier.fedexSmartpostCarrierInput.corporateCountryCode,
              corporate_email_address:
                carrier.fedexSmartpostCarrierInput.corporateEmailAddress,
              corporate_first_name:
                carrier.fedexSmartpostCarrierInput.corporateFirstName,
              corporate_job_title:
                carrier.fedexSmartpostCarrierInput.corporateJobTitle,
              corporate_last_name:
                carrier.fedexSmartpostCarrierInput.corporateLastName,
              corporate_phone_number:
                carrier.fedexSmartpostCarrierInput.corporatePhoneNumber,
              corporate_postal_code:
                carrier.fedexSmartpostCarrierInput.corporatePostalCode,
              corporate_state:
                carrier.fedexSmartpostCarrierInput.corporateState,
              corporate_streets:
                carrier.fedexSmartpostCarrierInput.corporateStreets,
            },
          };
          break;
        }
        case RedoCarrierName.SEKO_OMNIPARCEL:
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              returnsTestApiKey:
                carrier.sekoOmniParcelCarrierInput.returnsTestApiKey,
              returnsProductionApiKey:
                carrier.sekoOmniParcelCarrierInput.returnsProductionApiKey,
              outboundTestApiKey:
                carrier.sekoOmniParcelCarrierInput.outboundTestApiKey,
              outboundProductionApiKey:
                carrier.sekoOmniParcelCarrierInput.outboundProductionApiKey,
            },
          };
          break;
        case RedoCarrierName.GLS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.glsCarrierInput.accountNumber,
              password: carrier.glsCarrierInput.password,
              username: carrier.glsCarrierInput.username,
            },
          };
          break;
        }
        case RedoCarrierName.ASENDIA: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.asendiaCarrierInput.accountNumber,
              password: carrier.asendiaCarrierInput.password,
              username: carrier.asendiaCarrierInput.username,
              api_key: carrier.asendiaCarrierInput.apiKey,
              carrier_facility:
                carrier.asendiaCarrierInput.carrierFacility || undefined,
              sub_account_number:
                carrier.asendiaCarrierInput.subAccountNumber || undefined,
            },
          };
          break;
        }
        case RedoCarrierName.SPEE_DEE: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.speeDeeCarrierInput.accountNumber,
            },
          };
          break;
        }
        case RedoCarrierName.AMAZON: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: { redoCarrierName: carrier.redoCarrierName },
          };
          break;
        }
        case RedoCarrierName.USPS_SHIP: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              client_id: carrier.uspsShipCarrierInput.clientId,
              client_secret: carrier.uspsShipCarrierInput.clientSecret,
              account_number: carrier.uspsShipCarrierInput.accountNumber,
              customer_registration_id:
                carrier.uspsShipCarrierInput.customerRegistrationId,
              mailer_id: carrier.uspsShipCarrierInput.mailerId,
              manifest_id: carrier.uspsShipCarrierInput.manifestId,
            },
          };
          break;
        }
        case RedoCarrierName.PASSPORT_GLOBAL: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: RedoCarrierName.PASSPORT_GLOBAL,
              api_key: carrier.passportGlobalCarrierInput.apiKey,
              company_name: carrier.passportGlobalCarrierInput.companyName,
              email: carrier.passportGlobalCarrierInput.email,
              name: carrier.passportGlobalCarrierInput.name,
              phone: carrier.passportGlobalCarrierInput.phone,
            },
          };
          break;
        }
        case RedoCarrierName.ETA_EXPRESS: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              api_key: carrier.etaExpressCarrierInput.apiKey,
            },
          };
          break;
        }
        case RedoCarrierName.EPOST_GLOBAL: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: RedoCarrierName.EPOST_GLOBAL,
              account_number: carrier.epostGlobalCarrierInput.account_number,
            },
          };
          break;
        }
        case RedoCarrierName.USPS_DIRECT: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              account_number: carrier.uspsDirectCarrierInput.accountNumber,
              customer_registration_id:
                carrier.uspsDirectCarrierInput.customerRegistrationId,
              mailer_id: carrier.uspsDirectCarrierInput.mailerId,
              manifest_mailer_id:
                carrier.uspsDirectCarrierInput.manifestMailerId,
              rate_type: carrier.uspsDirectCarrierInput.rateType,
            },
          };
          break;
        }
        case RedoCarrierName.ONTRAC: {
          carrierToAdd = {
            name: carrier.name,
            carrierAccountType,
            data: {
              redoCarrierName: carrier.redoCarrierName,
              api_id: carrier.onTracCarrierInput.apiId,
              api_key: carrier.onTracCarrierInput.apiKey,
              facility: carrier.onTracCarrierInput.facility,
              injection_city: carrier.onTracCarrierInput.injectionCity,
              injection_contact: carrier.onTracCarrierInput.injectionContact,
              injection_country: carrier.onTracCarrierInput.injectionCountry,
              injection_facility_code:
                carrier.onTracCarrierInput.injectionFacilityCode,
              injection_instruction:
                carrier.onTracCarrierInput.injectionInstruction,
              injection_phone: carrier.onTracCarrierInput.injectionPhone,
              injection_state: carrier.onTracCarrierInput.injectionState,
              injection_street1: carrier.onTracCarrierInput.injectionStreet1,
              injection_street2: carrier.onTracCarrierInput.injectionStreet2,
              injection_zip: carrier.onTracCarrierInput.injectionZip,
              pickup_type: carrier.onTracCarrierInput.pickupType,
            },
          };
          break;
        }
        default:
          assertNever(carrier.redoCarrierName);
      }

      const response = await createCarrierAccount({ ...carrierToAdd });
      setSavedCarriers(getSavedCarriers(response));
      return { success: true };
    } catch (error) {
      const clientError = error as { statusCode: number; message: string };
      if (clientError.statusCode === 422) {
        toast(clientError.message, { variant: "error" });
      } else {
        toast(
          "Your input was rejected, please make sure everything is correct",
          { variant: "error" },
        );
      }
      return { success: false };
    }
  });

  const deleteCarrier = useHandler(async (carrierAccount: ICarrierAccount) => {
    try {
      const response = await deleteCarrierAccount({ carrierAccount });
      setSavedCarriers(getSavedCarriers(response));
    } catch (error) {
      toast("Failed to delete carrier account", { variant: "error" });
    }
  });

  const authenticateCarrier = useHandler(
    async (carrierAccount: ICarrierAccount) => {
      if ("oauthUrl" in carrierAccount && !!carrierAccount.oauthUrl) {
        window.open(carrierAccount.oauthUrl, "_blank");
      }
    },
  );

  return { addCarrier, deleteCarrier, authenticateCarrier };
}
